import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/pingdom')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Pingdom Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Pingdom: Pingdom has no free plan and prices by number of checks. Uptrack has 5 free monitors and checks 50 monitors every 30 seconds for $3/mo." },
      { property: 'og:title', content: 'Pingdom Alternative — Uptrack' },
      { property: 'og:description', content: "Compare Uptrack and Pingdom on pricing, status pages, check locations, and how each confirms downtime." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-pingdom.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/pingdom' },
    ],
    scripts: comparisonScripts('pingdom', "Pingdom"),
  }),
})

const data: ComparisonData = {
  competitor: "Pingdom",
  tagline: "Pingdom, from SolarWinds, offers uptime, transaction, and real user monitoring, priced by the number of checks. Uptrack checks every 30 seconds on every plan, from $3/mo.",
  heroDescription: "These facts come from Pingdom's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Starting price", uptrack: "$3/mo (50 monitors at 30s)", competitor: "From $10/mo (priced by number of checks)", uptrackWins: true },
    { feature: "Trial or guarantee", uptrack: "Free plan; 30-day money-back guarantee on paid plans", competitor: "30-day free trial" },
    { feature: "Down confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "A second probe confirms before a check is marked down" },
    { feature: "Probe locations", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "100+ probe servers" },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Public status pages included" },
    { feature: "Users", uptrack: "3 on Pro, 5 on Team, 15 on Business", competitor: "Unlimited" },
    { feature: "Real user monitoring", uptrack: "Not available", competitor: "Available" },
  ],

  painPoints: [
    {
      title: "Pricing by number of checks",
      description: "Pingdom's price grows with the number of checks, from $10/mo. Uptrack Pro includes 50 monitors at 30-second checks for $3/mo.",
    },
    {
      title: "How downtime is confirmed",
      description: "Pingdom marks a failure \"unconfirmed down\" until a second probe also fails. Uptrack waits for 2-5 consecutive failed checks, and several check locations must confirm.",
    },
    {
      title: "Browser flows on the entry plan",
      description: "Uptrack Pro includes 5 Playwright browser monitors for $3/mo. Each run follows a flow such as login or checkout and saves a screenshot at each step, so you see which step broke.",
    },
  ],

  differentiators: [
    "Alert confirmation: 2-5 consecutive failed checks, and several check locations must confirm. Time-based confirmation filters out short blips at one location.",
    "Flat pricing: $3/mo for 50 monitors at 30-second checks. The price does not grow with each monitor you add, up to the plan limit.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Status pages on every plan: 1 on free, 5 on Pro, unlimited on Team and Business, with email subscriber notifications on paid plans.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    `If you need transaction checks from many locations, Pingdom has 100+ probe servers. ${MONITOR_LOCATIONS.compare.pingdom}`,
    "If you need real user monitoring (RUM) or visitor analytics, Pingdom has this built in.",
    "If you already use other SolarWinds products, Pingdom fits into that stack.",
    "If you need unlimited users, Pingdom does not cap users. Uptrack plans include 3, 5, or 15 seats.",
  ],
}
